import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LOYALTY_CUSTOM_MEMBER_CODE_MIN,
  LOYALTY_POINT_NAME,
  maxCategoryDiscountPercent,
  parseCategoryDiscounts,
  validateCustomMemberCode,
  type CategoryDiscountMap,
} from '@cafe-pos/shared';
import { buildLoyaltyJoinUrl } from '../loyaltyJoin.js';
import { isSupabaseConfigured, supabase } from '../supabase.js';
import { formatNumber } from '../locale.js';

const ARMENIA_LOCAL_PHONE_LEN = 8;

function normalizeLocalPhoneInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, ARMENIA_LOCAL_PHONE_LEN);
}

function buildArmeniaPhone(localDigits: string): string | undefined {
  const local = normalizeLocalPhoneInput(localDigits);
  if (!local) return undefined;
  return `374${local}`;
}

function formatJoinDiscountBenefit(
  map: CategoryDiscountMap,
  flatPercent: number,
  categories: { id: string; name: string }[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  const entries = categories
    .map((c) => ({ name: c.name, pct: map[c.id] ?? 0 }))
    .filter((e) => e.pct > 0);
  if (entries.length > 0) {
    if (entries.length <= 3) {
      return entries.map((e) => t('loyaltyJoin.pctOffCategory', { pct: e.pct, name: e.name })).join(', ');
    }
    return t('loyaltyJoin.upToOff', { pct: maxCategoryDiscountPercent(map, flatPercent) });
  }
  if (flatPercent > 0) return t('loyaltyJoin.pctOffEvery', { pct: flatPercent });
  return null;
}

export function LoyaltyJoinView({
  clientId,
  linkSlug,
}: {
  clientId: string;
  linkSlug?: string;
}) {
  const { t, i18n } = useTranslation();
  const [cafeName, setCafeName] = useState('');
  const [linkName, setLinkName] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [categoryDiscounts, setCategoryDiscounts] = useState<CategoryDiscountMap>({});
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [collectCoins, setCollectCoins] = useState(true);
  const [allowCustomNumbers, setAllowCustomNumbers] = useState(false);
  const [earnAmdPerCoin, setEarnAmdPerCoin] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phone, setPhone] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [customMemberCode, setCustomMemberCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [memberCode, setMemberCode] = useState<string | null>(null);

  useEffect(() => {
    document.title = cafeName
      ? t('loyaltyJoin.pageTitleCafe', { cafeName })
      : t('loyaltyJoin.pageTitle');
    document.documentElement.style.colorScheme = 'light only';
    document.body.style.background = '#f5f6f8';
    return () => {
      document.documentElement.style.colorScheme = '';
      document.body.style.background = '';
    };
  }, [cafeName, t]);

  useEffect(() => {
    if (linkSlug) {
      const targetPath = `/join/${clientId}/${linkSlug}`;
      if (window.location.pathname !== targetPath) {
        window.history.replaceState(null, '', buildLoyaltyJoinUrl(clientId, linkSlug));
      }
    }
  }, [clientId, linkSlug]);

  useEffect(() => {
    let cancelled = false;

    async function loadJoinInfo() {
      if (!supabase) {
        setError(t('loyaltyJoin.notConfigured'));
        setLoading(false);
        return;
      }

      if (!linkSlug) {
        setError(t('loyaltyJoin.incompleteLink'));
        setLoading(false);
        return;
      }

      try {
        const [
          { data: client, error: clientErr },
          { data: settings, error: settingsErr },
          { data: link, error: linkErr },
          { data: menuCategories, error: categoriesErr },
        ] = await Promise.all([
          supabase.from('clients').select('name').eq('id', clientId).eq('active', true).maybeSingle(),
          supabase
            .from('loyalty_settings')
            .select('earn_amd_per_coin, platform_enabled, enabled')
            .eq('client_id', clientId)
            .maybeSingle(),
          supabase
            .from('loyalty_join_links')
            .select('name, discount_percent, category_discounts, collect_coins, allow_custom_numbers')
            .eq('client_id', clientId)
            .eq('slug', linkSlug)
            .eq('active', true)
            .maybeSingle(),
          supabase
            .from('categories')
            .select('id, name')
            .eq('client_id', clientId)
            .order('sort_order'),
        ]);

        if (cancelled) return;

        if (clientErr || settingsErr || linkErr || categoriesErr) {
          setError(
            clientErr?.message ??
              settingsErr?.message ??
              linkErr?.message ??
              categoriesErr?.message ??
              t('loyaltyJoin.loadFailed'),
          );
          return;
        }
        if (!client) {
          setError(t('loyaltyJoin.cafeNotFound'));
          return;
        }
        if (!settings?.platform_enabled) {
          setError(t('loyaltyJoin.notAvailable'));
          return;
        }
        if (!settings.enabled) {
          setError(t('loyaltyJoin.notOpen'));
          return;
        }
        if (!link) {
          setError(t('loyaltyJoin.invalidLink'));
          return;
        }

        setCafeName(client.name);
        setLinkName(link.name);
        setEarnAmdPerCoin(Number(settings.earn_amd_per_coin) || 50);
        setDiscountPercent(Number(link.discount_percent) || 0);
        setCategoryDiscounts(parseCategoryDiscounts(link.category_discounts));
        setCategories((menuCategories ?? []).map((c) => ({ id: c.id, name: c.name })));
        setCollectCoins(!!link.collect_coins);
        setAllowCustomNumbers(link.allow_custom_numbers === true);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t('loyaltyJoin.loadPageFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadJoinInfo();
    return () => {
      cancelled = true;
    };
  }, [clientId, linkSlug, t]);

  async function enroll() {
    if (!supabase || !linkSlug) return;

    const name = displayName.trim();
    if (!name) {
      setError(t('loyaltyJoin.enterName'));
      return;
    }

    if (allowCustomNumbers && customMemberCode.trim()) {
      const codeError = validateCustomMemberCode(customMemberCode);
      if (codeError) {
        setError(codeError);
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('loyalty-enroll', {
        body: {
          clientId,
          linkSlug,
          displayName: name,
          phone: buildArmeniaPhone(phone),
          memberCode:
            allowCustomNumbers && customMemberCode.trim()
              ? customMemberCode.trim()
              : undefined,
        },
      });
      if (fnErr) {
        const msg = fnErr.message.includes('not found')
          ? t('loyaltyJoin.enrollNotConfigured')
          : fnErr.message;
        setError(msg);
        return;
      }
      const result = data as {
        shareUrl?: string | null;
        memberCode?: string;
        error?: string;
      };
      if (result.error) {
        setError(result.error);
        return;
      }
      if (!result.memberCode || !result.shareUrl) {
        setError(t('loyaltyJoin.noWalletLink'));
        return;
      }
      setMemberCode(result.memberCode);
      setShareUrl(result.shareUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('loyaltyJoin.signupFailed'));
    } finally {
      setBusy(false);
    }
  }

  const discountBenefit = formatJoinDiscountBenefit(
    categoryDiscounts,
    discountPercent,
    categories,
    t,
  );

  const benefitNote = (
    <>
      {discountBenefit && <>{t('loyaltyJoin.youGet', { benefit: discountBenefit })}</>}
      {collectCoins
        ? t('loyaltyJoin.earnPoints', {
            pointName: LOYALTY_POINT_NAME.toLowerCase(),
            earnRate: formatNumber(earnAmdPerCoin, i18n.language),
          })
        : t('loyaltyJoin.discountOnly')}
    </>
  );

  if (!isSupabaseConfigured) {
    return (
      <div className="customer-screen">
        <div className="customer-card customer-card-centered">
          <div className="customer-brand">Orio</div>
          <p className="customer-error">{t('loyaltyJoin.unavailable')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="customer-screen">
        <div className="customer-card customer-card-centered">
          <p className="customer-muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !cafeName) {
    return (
      <div className="customer-screen">
        <div className="customer-card customer-card-centered">
          <div className="customer-brand">Orio</div>
          <p className="customer-error">{error}</p>
        </div>
      </div>
    );
  }

  if (memberCode && shareUrl) {
    return (
      <div className="customer-screen">
        <div className="customer-card customer-card-centered">
          <div className="customer-brand">{cafeName}</div>
          <h1 className="customer-title">{t('loyaltyJoin.successTitle')}</h1>
          <p className="customer-number">
            {t('loyaltyJoin.customerNumber', { code: memberCode })}
          </p>
          <p className="customer-muted">{t('loyaltyJoin.savePass')}</p>
          <a className="customer-btn" href={shareUrl}>
            {t('loyaltyJoin.addWallet')}
          </a>
          <p className="customer-muted customer-footnote">{benefitNote}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-screen">
      <div className="customer-card">
        <div className="customer-brand">{cafeName}</div>
        <h1 className="customer-title">{linkName || t('loyaltyJoin.defaultTitle')}</h1>
        <p className="customer-lead">
          {discountBenefit && (
            <>
              <strong style={{ color: '#1c1c1e' }}>{discountBenefit}</strong>
              {collectCoins ? t('loyaltyJoin.plus') : '. '}
            </>
          )}
          {collectCoins ? t('loyaltyJoin.leadPoints') : t('loyaltyJoin.leadDiscount')}
        </p>
        {error && <p className="customer-error">{error}</p>}
        <label className="customer-field">
          <span className="customer-field-label">{t('loyaltyJoin.nameLabel')}</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('loyaltyJoin.namePlaceholder')}
            required
            autoComplete="name"
          />
        </label>
        <label className="customer-field">
          <span className="customer-field-label">{t('loyaltyJoin.phoneLabel')}</span>
          <div className="customer-phone-input">
            <span className="customer-phone-prefix" aria-hidden>
              +374
            </span>
            <input
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(normalizeLocalPhoneInput(e.target.value))}
              placeholder={t('loyaltyJoin.phonePlaceholder')}
              autoComplete="tel-national"
              aria-label={t('loyaltyJoin.phoneAria')}
            />
          </div>
        </label>
        {allowCustomNumbers && (
          <label className="customer-field">
            <span className="customer-field-label">
              {t('loyaltyJoin.customNumberLabel', { min: LOYALTY_CUSTOM_MEMBER_CODE_MIN })}
            </span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={customMemberCode}
              onChange={(e) => setCustomMemberCode(e.target.value.replace(/\D/g, ''))}
              placeholder={t('loyaltyJoin.customNumberPlaceholder', {
                min: LOYALTY_CUSTOM_MEMBER_CODE_MIN,
              })}
            />
          </label>
        )}
        <button
          type="button"
          className="customer-btn"
          onClick={() => void enroll()}
          disabled={busy || !displayName.trim()}
        >
          {busy ? t('loyaltyJoin.signingUp') : t('loyaltyJoin.signUp')}
        </button>
      </div>
    </div>
  );
}
